import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import { EnvironmentRuntime, ProjectEnvironment } from '../../services/projectEnvironment';
import { getRelativeTime } from '../../utils/dateUtils';
import { displayEnvironmentName, isBusyStatus, isDeployed, runtimeAction, urlLabel } from './types';
import { EnvironmentBadge } from './StatusBadge';

interface EnvironmentListViewProps {
  loading: boolean;
  error: string | null;
  environments: ProjectEnvironment[];
  /** Live container state, keyed by environment id. */
  runtime: Record<string, EnvironmentRuntime>;
  /** Environment whose start/stop request is in flight. */
  runtimeActionId: string | null;
  deletingId: string | null;
  actionError: string | null;
  isBuilding: boolean;
  onStartStop: (environment: ProjectEnvironment, action: 'start' | 'stop') => void;
  onRetry: () => void;
  onEdit: (environment: ProjectEnvironment) => void;
  onDelete: (environment: ProjectEnvironment) => void;
  onOpenDetails: (environment: ProjectEnvironment) => void;
  onCreate: () => void;
  project: DevProject | null;
}

/** The environment list: create button, per-environment cards with status/edit/delete. */
export const EnvironmentListView: React.FC<EnvironmentListViewProps> = ({
  loading,
  error,
  environments,
  runtime,
  runtimeActionId,
  deletingId,
  actionError,
  isBuilding,
  onStartStop,
  onRetry,
  onEdit,
  onDelete,
  onOpenDetails,
  onCreate,
  project,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-bg-v2-lg w-full">
        {[0, 1].map((index) => (
          <div
            key={index}
            className="h-[76px] skeleton-shimmer rounded-bg-v2-md w-full animate-fade-in-up opacity-0"
            style={{ animationDelay: `${index * 0.08}s`, animationFillMode: 'forwards' }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg">
        <Icon name={IconName.Alert} size="xxl" className="text-text-error-primary" inheritColor />
        <Text variant="text-md" weight="semibold" color="primary">
          Error Loading Environments
        </Text>
        <Text variant="text-sm" color="secondary" align="center">
          {error}
        </Text>
        <button
          onClick={onRetry}
          className="mt-bg-v2-md px-bg-v2-xl py-bg-v2-md bg-bg-brand-solid text-text-white rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Text variant="text-sm" weight="medium" color="white">
            Try Again
          </Text>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-bg-v2-lg w-full">
      <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
        <button
          onClick={onCreate}
          className="w-full flex items-center justify-center gap-bg-v2-xs px-bg-v2-lg py-bg-v2-md bg-bg-brand-solid text-text-white rounded-bg-v2-md hover:bg-bg-brand-solid-hover transition-colors"
        >
          <Icon name={IconName.Plus} size="sm" className="text-text-white" inheritColor />
          <Text variant="text-sm" weight="medium" color="white">
            Create environment
          </Text>
        </button>
      </IfHasPermission>

      {actionError && (
        <div className="flex items-center gap-bg-v2-xs p-bg-v2-md bg-bg-error-primary border border-border-error-subtle rounded-bg-v2-sm">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0" inheritColor />
          <Text variant="text-xs" color="error">
            {actionError}
          </Text>
        </div>
      )}

      {environments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-bg-v2-8xl gap-bg-v2-lg text-center w-full">
          <Icon name={IconName.Environment} size="xxl" className="text-text-quaternary" inheritColor />
          <Text variant="text-md" weight="semibold" color="primary" align="center">
            No Environments
          </Text>
          <Text variant="text-sm" color="secondary" align="center" className="max-w-[300px]">
            Create an environment to test this project on its own URL without touching the live app.
          </Text>
        </div>
      ) : (
        environments.map((environment, index) => {
          const busy = isBusyStatus(environment.status);
          const deployed = isDeployed(environment.status);
          const label = environment.url ? urlLabel(environment.url) : null;
          const envRuntime = runtime[environment.id];
          const action = deployed && !busy ? runtimeAction(envRuntime, environment.status) : null;
          const actionPending =
            runtimeActionId === environment.id || envRuntime?.in_progress === true;
          return (
            <div
              key={environment.id}
              onClick={() => onOpenDetails(environment)}
              className="bg-bg-primary border border-border-secondary box-border flex flex-col gap-bg-v2-lg p-bg-v2-xl rounded-bg-v2-md w-full cursor-pointer hover:border-border-brand transition-colors animate-fade-in-up opacity-0"
              style={{
                boxShadow:
                  '0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))',
                animationDelay: `${index * 0.08}s`,
                animationFillMode: 'forwards',
              }}
            >
              <div className="flex gap-bg-v2-lg items-start w-full">
                <div className="flex flex-col items-start flex-1 min-w-0">
                  <Text variant="text-sm" weight="semibold" color="primary">
                    {displayEnvironmentName(environment.environment_name)}
                  </Text>
                  {deployed && environment.url ? (
                    <a
                      href={environment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="mt-bg-v2-sm inline-flex items-center gap-bg-v2-xs text-border-brand hover:underline min-w-0 max-w-full"
                    >
                      <Text variant="text-xs" className="!text-border-brand truncate">
                        {label}
                      </Text>
                      <Icon name={IconName.ExternalLink} size="xs" inheritColor className="shrink-0" />
                    </a>
                  ) : (
                    (label || busy) && (
                      <Text variant="text-xs" color="tertiary" className="mt-bg-v2-sm truncate max-w-full">
                        {label
                          ? `${label}${busy ? ' · available once deployed' : ''}`
                          : 'URL available once deployed'}
                      </Text>
                    )
                  )}
                </div>
                <div className="flex items-center gap-bg-v2-xs shrink-0">
                  <IfHasPermission
                    permissions={project?.permissions}
                    requires={Permissions.UpdateProjectPermission}
                  >
                    {deployed && (action || actionPending) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (action && !actionPending) onStartStop(environment, action);
                        }}
                        disabled={!action || actionPending}
                        className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
                        title={
                          actionPending || !action
                            ? 'Start or stop in progress'
                            : action === 'start'
                            ? 'Start environment'
                            : 'Stop environment'
                        }
                      >
                        {actionPending || !action ? (
                          <Icon name={IconName.Loading} size="xs" spin className="text-text-tertiary" inheritColor />
                        ) : (
                          <Icon
                            name={action === 'start' ? IconName.Run : IconName.Stop}
                            size="xs"
                            className="text-text-tertiary"
                            inheritColor
                          />
                        )}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(environment);
                      }}
                      disabled={busy || deletingId === environment.id}
                      className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
                      title={busy ? 'Unavailable while a build is running' : 'Edit environment'}
                    >
                      <Icon name={IconName.Edit} size="xs" className="text-text-tertiary" inheritColor />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(environment);
                      }}
                      disabled={busy || deletingId === environment.id}
                      className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-error-primary transition-colors disabled:opacity-50"
                      title={busy ? 'Unavailable while a build is running' : 'Delete environment'}
                    >
                      {deletingId === environment.id ? (
                        <Icon name={IconName.Loading} size="xs" spin className="text-text-tertiary" inheritColor />
                      ) : (
                        <Icon name={IconName.Delete} size="xs" className="text-text-tertiary hover:text-text-error-primary transition-colors" inheritColor />
                      )}
                    </button>
                  </IfHasPermission>
                </div>
              </div>
              <div className="border-t border-border-secondary w-full" />
              <div className="flex items-center justify-between gap-bg-v2-md">
                <div className="flex items-center gap-bg-v2-xs min-w-0">
                  <Icon name={IconName.ClockCircle} size="xs" className="text-text-tertiary shrink-0" inheritColor />
                  <Text variant="text-xs" color="tertiary">
                    {environment.deployed_at
                      ? `Deployed ${getRelativeTime(environment.deployed_at)}`
                      : 'Not deployed yet'}
                  </Text>
                </div>
                <EnvironmentBadge status={environment.status} runtime={envRuntime} />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default EnvironmentListView;
